import { IconButton } from '@creditornot/cb-button';
import { MoreHorizontal } from '@creditornot/cb-icons';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { LegacyPopover } from '@creditornot/cb-popover';
import { useMutation } from '@tanstack/react-query';
import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ConfirmModal } from 'common/components';
import ErrorToastContent from 'common/components/ErrorToastContent';
import { deleteOption } from 'modules/shared-menu/api';

import { useSelectionData } from '../../../../hooks/useSelectionData';

type Props = {
  onCopy?: (optionId: string) => void;
  optionId: string;
};

const Root = styled.div``;

const StyledPopover = styled(LegacyPopover).attrs({
  placement: 'bottom-start',
  renderArrow: false,
})`
  display: flex;
  align-items: center;
`;

const ToggleButton = styled(IconButton).attrs({ variant: 'blue', size: 'tiny' })``;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 200px;
  padding: 0 8px;
`;

export const OptionActions = ({ onCopy, optionId }: Props) => {
  const { selection, updateSelectionData } = useSelectionData();

  const [show, setShow] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleClose = useCallback(() => setShow(false), []);

  const { mutate, isLoading } = useMutation(() => deleteOption(selection.id, optionId), {
    onSuccess: updatedFullSelection => {
      setIsDeleteModalOpen(false);
      updateSelectionData({ actionType: 'updateSelectionContainer', payload: updatedFullSelection });
      toast.success(<FormattedMessage id="toast.success" />);
    },
    onError: (error: any) => {
      toast.error(<ErrorToastContent error={error} />);
    },
  });

  return (
    <Root onClick={e => e.stopPropagation()}>
      <StyledPopover
        content={
          <PopoverContent>
            <MenuItemGroup>
              <MenuActionItem onClick={() => onCopy?.(optionId)} variant="black">
                <FormattedMessage id="menu-option.create-copy" />
              </MenuActionItem>

              <MenuActionItem onClick={() => setIsDeleteModalOpen(true)} variant="red">
                <FormattedMessage id="menu-option.delete" />
              </MenuActionItem>
            </MenuItemGroup>
          </PopoverContent>
        }
        onClickOutside={handleClose}
        onClickInside={handleClose}
        show={show}
      >
        <ToggleButton
          onClick={e => {
            e.preventDefault();
            setShow(!show);
          }}
        >
          <MoreHorizontal />
        </ToggleButton>
      </StyledPopover>
      {isDeleteModalOpen && (
        <ConfirmModal
          header={<FormattedMessage id="menu-option.delete" />}
          loading={isLoading}
          onCancel={() => setIsDeleteModalOpen(false)}
          onConfirm={() => mutate()}
          open
        />
      )}
    </Root>
  );
};
